/**
 * Extension contribution render surfaces. These thin components turn registry
 * entries into rendered React, wiring each extension's `ExtensionUIProps` /
 * translator from the mounted `HostServices`. They no-op gracefully when no
 * `<ExtensionShellProvider>` is mounted.
 */
import type {
    FloatingPanelProps,
    SettingsPanelProps,
    UnitConfigContext,
} from "@magic-md/extension-sdk";
import {
    cloneElement,
    createElement,
    Fragment,
    isValidElement,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    type MouseEvent as ReactMouseEvent,
    type ReactNode,
} from "react";
import { GripHorizontal, X } from "lucide-react";
import { useExtensionShellServices } from "./extension-shell-context";
import {
    useExtensionHostStore,
    type ExtensionFloatingPanelInstance,
} from "./extension-host-store";
import { contributionRegistry } from "./contribution-registry";
import { renderExtensionActivityIcon } from "./extension-activity-icon";
import {
    makeExtensionTranslator,
    useExtensionFloatingPanels,
    useExtensionSettingsPanelIds,
    useExtensionSidebarPanels,
    useExtensionStatusBarItems,
    useExtensionUIProps,
    useExtensionUnitConfigActions,
} from "./useExtensionContributions";

const FLOATING_MIN_WIDTH = 300;
const FLOATING_MIN_HEIGHT = 220;
const EXTENSION_STATUS_BAR_ICON_SIZE = 11;

function resizeExtensionStatusBarIcon(icon: ReactNode): ReactNode {
    if (!isValidElement<Record<string, unknown>>(icon)) return icon;
    if ("size" in icon.props) {
        return cloneElement(icon, { size: EXTENSION_STATUS_BAR_ICON_SIZE });
    }
    if (icon.type === "svg") {
        return cloneElement(icon, {
            width: EXTENSION_STATUS_BAR_ICON_SIZE,
            height: EXTENSION_STATUS_BAR_ICON_SIZE,
        });
    }
    return icon;
}

function clampFloatingInstance(
    instance: ExtensionFloatingPanelInstance,
): ExtensionFloatingPanelInstance {
    const viewportWidth = typeof window === "undefined" ? 1280 : window.innerWidth;
    const viewportHeight = typeof window === "undefined" ? 800 : window.innerHeight;
    const width = Math.min(
        Math.max(instance.width, FLOATING_MIN_WIDTH),
        Math.max(FLOATING_MIN_WIDTH, viewportWidth - 24),
    );
    const height = Math.min(
        Math.max(instance.height, FLOATING_MIN_HEIGHT),
        Math.max(FLOATING_MIN_HEIGHT, viewportHeight - 24),
    );
    return {
        ...instance,
        width,
        height,
        x: Math.min(Math.max(12, instance.x), Math.max(12, viewportWidth - width - 12)),
        y: Math.min(Math.max(12, instance.y), Math.max(12, viewportHeight - height - 12)),
    };
}

/* ── Status bar ── */

export interface ExtensionStatusBarItemsProps {
    align?: "left" | "center" | "right";
}

/** Render every active extension status-bar item for one alignment. */
export function ExtensionStatusBarItems({
    align,
}: ExtensionStatusBarItemsProps): ReactNode {
    const services = useExtensionShellServices();
    const items = useExtensionStatusBarItems(align);
    if (items.length === 0) return null;
    const sidebarPanels = contributionRegistry.sidebarPanels();
    return createElement(
        Fragment,
        null,
        ...items.map(({ extensionId, item }) => {
            const sidebarPanel = sidebarPanels.find(
                (entry) => entry.extensionId === extensionId,
            );
            return createElement(
                Fragment,
                { key: `${extensionId}:${item.id}` },
                item.render({
                    extensionId,
                    icon: resizeExtensionStatusBarIcon(
                        renderExtensionActivityIcon(
                            extensionId,
                            sidebarPanel?.panel.icon,
                            EXTENSION_STATUS_BAR_ICON_SIZE,
                        ),
                    ),
                    t: makeExtensionTranslator(services, extensionId),
                }),
            );
        }),
    );
}

/* ── Sidebar panel ── */

export interface ExtensionSidebarPanelViewProps {
    extensionId: string;
    panelId: string;
}

/** Render a single extension sidebar panel by id. */
export function ExtensionSidebarPanelView({
    extensionId,
    panelId,
}: ExtensionSidebarPanelViewProps): ReactNode {
    const panels = useExtensionSidebarPanels();
    const props = useExtensionUIProps(extensionId);
    const entry = panels.find(
        (p) => p.extensionId === extensionId && p.panel.id === panelId,
    );
    if (!entry || !props) return null;
    return createElement(entry.panel.component, props);
}

/* ── Floating panels ── */

export function ExtensionFloatingPanels(): ReactNode {
    const instances = useExtensionHostStore((state) => state.floatingPanels);
    const panels = useExtensionFloatingPanels();
    const close = useExtensionHostStore((state) => state.closeFloatingPanel);
    if (instances.length === 0) return null;

    return createElement(
        Fragment,
        null,
        ...instances.map((instance) => {
            const entry = panels.find(
                (item) =>
                    item.extensionId === instance.extensionId &&
                    item.panel.id === instance.panelId,
            );
            if (!entry) return null;
            return createElement(ExtensionFloatingPanelFrame, {
                key: instance.instanceId,
                instance,
                title: instance.title ?? entry.panel.title,
                onClose: () => close(instance.instanceId),
            });
        }),
    );
}

export interface ExtensionFloatingPanelStandaloneProps {
    instance: ExtensionFloatingPanelInstance;
    onClose?: () => void;
}

/** Render one extension floating panel without the in-app movable frame. */
export function ExtensionFloatingPanelStandalone({
    instance,
    onClose,
}: ExtensionFloatingPanelStandaloneProps): ReactNode {
    const panels = useExtensionFloatingPanels();
    const props = useExtensionUIProps(instance.extensionId);
    const close = useExtensionHostStore((state) => state.closeFloatingPanel);
    const entry = panels.find(
        (item) =>
            item.extensionId === instance.extensionId &&
            item.panel.id === instance.panelId,
    );
    if (!entry || !props) return null;
    const componentProps: FloatingPanelProps = {
        ...props,
        instanceId: instance.instanceId,
        payload: instance.payload,
        close: onClose ?? (() => close(instance.instanceId)),
    };
    return createElement(entry.panel.component, componentProps);
}

function ExtensionFloatingPanelFrame({
    instance,
    title,
    onClose,
}: {
    instance: ExtensionFloatingPanelInstance;
    title: string;
    onClose: () => void;
}): ReactNode {
    const panels = useExtensionFloatingPanels();
    const props = useExtensionUIProps(instance.extensionId);
    const move = useExtensionHostStore((state) => state.moveFloatingPanel);
    const resize = useExtensionHostStore((state) => state.resizeFloatingPanel);
    const close = useExtensionHostStore((state) => state.closeFloatingPanel);
    const setHeaderActions = useExtensionHostStore(
        (state) => state.setFloatingPanelHeaderActions,
    );
    const dragRef = useRef<{
        startX: number;
        startY: number;
        x: number;
        y: number;
    } | null>(null);
    const resizeRef = useRef<{
        startX: number;
        startY: number;
        width: number;
        height: number;
    } | null>(null);
    const clamped = clampFloatingInstance(instance);
    const entry = panels.find(
        (item) =>
            item.extensionId === instance.extensionId &&
            item.panel.id === instance.panelId,
    );
    const setInstanceHeaderActions = useCallback(
        (actions: ReactNode | null) =>
            setHeaderActions(instance.instanceId, actions),
        [instance.instanceId, setHeaderActions],
    );

    const startDrag = useCallback(
        (event: ReactMouseEvent<HTMLDivElement>) => {
            if (event.button !== 0) return;
            event.preventDefault();
            dragRef.current = {
                startX: event.clientX,
                startY: event.clientY,
                x: clamped.x,
                y: clamped.y,
            };
            document.body.style.userSelect = "none";
        },
        [clamped.x, clamped.y],
    );

    const startResize = useCallback(
        (event: ReactMouseEvent<HTMLButtonElement>) => {
            if (event.button !== 0) return;
            event.preventDefault();
            event.stopPropagation();
            resizeRef.current = {
                startX: event.clientX,
                startY: event.clientY,
                width: clamped.width,
                height: clamped.height,
            };
            document.body.style.cursor = "nwse-resize";
            document.body.style.userSelect = "none";
        },
        [clamped.height, clamped.width],
    );

    useEffect(() => {
        const handleMouseMove = (event: MouseEvent) => {
            if (dragRef.current) {
                const next = clampFloatingInstance({
                    ...clamped,
                    x: dragRef.current.x + event.clientX - dragRef.current.startX,
                    y: dragRef.current.y + event.clientY - dragRef.current.startY,
                });
                move(instance.instanceId, next.x, next.y);
                return;
            }
            if (resizeRef.current) {
                const next = clampFloatingInstance({
                    ...clamped,
                    width:
                        resizeRef.current.width +
                        event.clientX -
                        resizeRef.current.startX,
                    height:
                        resizeRef.current.height +
                        event.clientY -
                        resizeRef.current.startY,
                });
                resize(instance.instanceId, next.width, next.height);
            }
        };
        const handleMouseUp = () => {
            if (!dragRef.current && !resizeRef.current) return;
            dragRef.current = null;
            resizeRef.current = null;
            document.body.style.cursor = "";
            document.body.style.userSelect = "";
        };
        window.addEventListener("mousemove", handleMouseMove);
        window.addEventListener("mouseup", handleMouseUp);
        return () => {
            window.removeEventListener("mousemove", handleMouseMove);
            window.removeEventListener("mouseup", handleMouseUp);
            document.body.style.cursor = "";
            document.body.style.userSelect = "";
        };
    }, [clamped, instance.instanceId, move, resize]);

    if (!entry || !props) return null;
    const componentProps: FloatingPanelProps = {
        ...props,
        instanceId: instance.instanceId,
        payload: instance.payload,
        close: () => close(instance.instanceId),
        setHeaderActions: setInstanceHeaderActions,
    };
    return createElement(
        "div",
        {
            className:
                "fixed z-[70] flex min-h-0 flex-col overflow-hidden rounded-lg border border-border bg-background shadow-2xl",
            style: {
                left: clamped.x,
                top: clamped.y,
                width: clamped.width,
                height: clamped.height,
            },
        },
        createElement(
            "div",
            {
                className:
                    "flex h-9 shrink-0 cursor-grab select-none items-center gap-1 border-b border-border bg-muted/30 px-2 active:cursor-grabbing",
                onMouseDown: startDrag,
            },
            createElement(GripHorizontal, {
                size: 14,
                className: "shrink-0 text-muted-foreground",
            }),
            createElement(
                "span",
                { className: "min-w-0 flex-1 truncate text-[11px] font-medium" },
                title,
            ),
            instance.headerActions
                ? createElement(
                    "div",
                    {
                        className: "flex shrink-0 items-center gap-1",
                        onMouseDown: (event: ReactMouseEvent) =>
                            event.stopPropagation(),
                    },
                    instance.headerActions,
                )
                : null,
            createElement(
                "button",
                {
                    type: "button",
                    className:
                        "flex h-6 w-6 items-center justify-center rounded text-muted-foreground hover:bg-muted hover:text-foreground",
                    onMouseDown: (event: ReactMouseEvent) => event.stopPropagation(),
                    onClick: onClose,
                    title: "Close",
                },
                createElement(X, { size: 13 }),
            ),
        ),
        createElement(
            "div",
            { className: "min-h-0 flex-1 overflow-hidden" },
            createElement(entry.panel.component, componentProps),
        ),
        createElement("button", {
            type: "button",
            className:
                "absolute bottom-0 right-0 h-4 w-4 cursor-nwse-resize rounded-tl border-l border-t border-border bg-muted/40",
            onMouseDown: startResize,
            "aria-label": "Resize",
        }),
    );
}

/* ── Unit config actions (DefinitionPopover) ── */

export interface ExtensionUnitConfigActionsProps {
    unit: UnitConfigContext;
    /** Optional className for each action button. */
    className?: string;
    /** Compact icon-only rendering for dense config headers. */
    showLabels?: boolean;
}

/**
 * Render contributed unit-config action buttons for a definition/unit. Used by
 * the DefinitionPopover. Renders nothing when no actions match.
 */
export function ExtensionUnitConfigActions({
    unit,
    className,
    showLabels = true,
}: ExtensionUnitConfigActionsProps): ReactNode {
    const actions = useExtensionUnitConfigActions(unit);
    const run = useCallback(
        (handler: (u: UnitConfigContext) => void | Promise<void>) => {
            void handler(unit);
        },
        [unit],
    );
    if (actions.length === 0) return null;
    return createElement(
        Fragment,
        null,
        ...actions.map(({ extensionId, action }) =>
            createElement(
                "button",
                {
                    key: `${extensionId}:${action.id}`,
                    type: "button",
                    onClick: () => run(action.handler),
                    title: action.label,
                    className:
                        className ??
                        "flex items-center gap-1 px-2 py-1 rounded text-[11px] text-muted-foreground hover:text-foreground hover:bg-muted transition-colors",
                },
                action.icon
                    ? createElement("span", { "aria-hidden": true }, action.icon)
                    : null,
                showLabels ? createElement("span", null, action.label) : null,
            ),
        ),
    );
}

/* ── Settings panels (Settings Center) ── */

export interface ExtensionSettingsPanelsProps {
    /** Wrapper for each panel; receives extensionId + the rendered panel. */
    renderWrapper?: (extensionId: string, panel: ReactNode) => ReactNode;
}

/**
 * Render every extension-contributed settings panel, wiring settings get/set
 * through the host. Used by the Settings Center "Extensions" section.
 */
export function ExtensionSettingsPanels({
    renderWrapper,
}: ExtensionSettingsPanelsProps): ReactNode {
    const ids = useExtensionSettingsPanelIds();
    if (ids.length === 0) return null;
    return createElement(
        Fragment,
        null,
        ...ids.map((id) =>
            createElement(ExtensionSettingsPanelView, {
                key: id,
                extensionId: id,
                renderWrapper,
            }),
        ),
    );
}

export interface ExtensionSettingsPanelViewProps {
    extensionId: string;
    renderWrapper?: (extensionId: string, panel: ReactNode) => ReactNode;
}

export function ExtensionSettingsPanelView({
    extensionId,
    renderWrapper,
}: ExtensionSettingsPanelViewProps): ReactNode {
    const services = useExtensionShellServices();
    const storedSettings = useExtensionHostStore(
        (state) => state.settings[extensionId],
    );
    const defaultSettings = useExtensionHostStore(
        (state) => state.defaults[extensionId],
    );
    const settings = useMemo(
        () => ({
            ...(defaultSettings ?? {}),
            ...(services?.settings.get(extensionId) ?? {}),
            ...(storedSettings ?? {}),
        }),
        [defaultSettings, extensionId, services, storedSettings],
    );
    const Panel = services
        ? contributionRegistry.settingsPanel(extensionId)
        : undefined;
    if (!services || !Panel) return null;

    const props: SettingsPanelProps = {
        extensionId,
        settings,
        onChange: (next) => {
            void services.runtime.updateSettings(extensionId, next);
        },
        t: makeExtensionTranslator(services, extensionId),
    };
    const node = createElement(Panel, props);
    return renderWrapper ? renderWrapper(extensionId, node) : node;
}
